import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { UtilsService } from './utils.service';
import { Constants } from '../constants/constants';
/**
 * AI Upsell — frontend service.
 *
 * Hard dead-zone when the org has not enabled `aiUpsellEnabled`:
 * every public method returns an empty / null observable WITHOUT
 * making an HTTP call. This is the primary frontend gate; the backend
 * 403 is defense-in-depth in case the flag is flipped mid-session.
 *
 * Role gating helpers:
 *   - isStaffSurface()    — Admin / Manager / Server (whisper UI)
 *   - isCustomerSurface() — Kiosk / Table / QROrder  (popup UI)
 */
export interface UpsellSuggestion {
  itemId: string;
  name: string;
  price: number;
  imageData?: string;
  score: number;
  reason: string;
  confidence: number | null;
}

export interface UpsellMetrics {
  totalImpressions: number;
  totalAccepts: number;
  totalRejects: number;
  acceptanceRate: number;
  attributedRevenueCents: number;
  topPairs: Array<{
    anchorItemId: string;
    anchorName: string | null;
    suggestedItemId: string;
    suggestedName: string | null;
    suggestedPrice: number;
    shown: number;
    accepted: number;
    rejected: number;
    acceptanceRate: number | null;
  }>;
}

@Injectable({ providedIn: 'root' })
export class UpsellService {
  constructor(private http: HttpClient, private utilsService: UtilsService) {}

  /**
   * Returns true when the current user's org has aiUpsellEnabled set
   * truthy. Every UI surface MUST call this before rendering any upsell
   * content. Truthy (rather than === true) so a value that round-trips
   * through JSON / form-binding as 1 / "true" / etc. still satisfies
   * the gate — strict equality silently failed for users who'd flipped
   * the toggle on.
   */
  public isEnabled(): boolean {
    const user = this.utilsService.getCurrentUser();
    return !!user?.orgSettings?.featuresEnabled?.aiUpsellEnabled;
  }

  /** Roles that see the small whisper line. */
  public isStaffSurface(): boolean {
    const role = this.utilsService.getSelectedRoleFromSession();
    return role === Constants.ADMIN || role === Constants.MANAGER || role === Constants.SERVER;
  }

  /** Roles that see the customer-facing add-to-cart popup. */
  public isCustomerSurface(): boolean {
    const role = this.utilsService.getSelectedRoleFromSession();
    return role === Constants.KIOSK || role === Constants.TABLE || role === Constants.QR_ORDER;
  }

  private getOrgUuid(): string | null {
    const user = this.utilsService.getCurrentUser();
    return user?.orgUuid || user?.orgSettings?.orgUuid || null;
  }

  /**
   * Get the top-N companion suggestions for the current cart. Returns []
   * (without an HTTP call) when the feature is off, the cart is empty, or
   * the org cannot be resolved — making this safe to call unconditionally.
   */
  getSuggestions(cartItemIds: string[], limit = 1): Observable<UpsellSuggestion[]> {
    if (!this.isEnabled()) return of([]);
    const orgUuid = this.getOrgUuid();
    if (!orgUuid || !cartItemIds || cartItemIds.length === 0) return of([]);
    const ids = cartItemIds.filter(Boolean).join(',');
    const params = new HttpParams().set('cartItemIds', ids).set('limit', String(limit));
    return this.http
      .get<{ suggestions: UpsellSuggestion[] }>(
        `${environment.apiUrl}/upsell/${orgUuid}/suggest`,
        { params },
      )
      .pipe(
        map(r => r?.suggestions || []),
        // Network blip / 403 race shouldn't break the cashier — silently
        // collapse to no suggestions and let the caller render the cart.
        catchError(() => of([])),
      );
  }

  /**
   * Record that a suggestion was shown ('shown'), tap-to-added by staff /
   * accepted by a customer ('accepted'), or dismissed ('rejected').
   * No-ops when feature off or no org.
   */
  recordFeedback(
    anchorItemIds: string[],
    suggestedItemId: string,
    decision: 'shown' | 'accepted' | 'rejected',
  ): Observable<{ updated: number }> {
    if (!this.isEnabled()) return of({ updated: 0 });
    const orgUuid = this.getOrgUuid();
    if (!orgUuid || !suggestedItemId || !anchorItemIds || anchorItemIds.length === 0) {
      return of({ updated: 0 });
    }
    return this.http
      .post<{ updated: number }>(`${environment.apiUrl}/upsell/${orgUuid}/feedback`, {
        anchorItemIds: anchorItemIds.filter(Boolean),
        suggestedItemId,
        decision,
      })
      .pipe(catchError(() => of({ updated: 0 })));
  }

  /**
   * One-shot orchestrator for customer-facing surfaces (kiosk / QR / table).
   * Pulls a single suggestion and opens UpsellPopupComponent; resolves with
   * `{ accepted, suggestion? }` so the host can append to the cart and
   * continue checkout. No-ops (resolves null) when:
   *   - feature flag off
   *   - role is not a customer surface
   *   - no suggestion was returned
   * Intended call site: just before navigating to payment.
   */
  async maybeShowCustomerPopup(
    cartItemIds: string[],
    dialog: MatDialog,
  ): Promise<{ accepted: boolean; suggestion?: UpsellSuggestion } | null> {
    if (!this.isEnabled() || !this.isCustomerSurface()) return null;
    const suggestions = await this.getSuggestions(cartItemIds, 1).toPromise();
    const top = suggestions && suggestions[0];
    if (!top) return null;

    const { UpsellPopupComponent } = await import('../shared/components/upsell/upsell-popup.component');
    const ref = dialog.open(UpsellPopupComponent, {
      data: { suggestion: top, cartItemIds },
      panelClass: 'upsell-popup-panel',
      disableClose: false,
    });
    return ref.afterClosed().toPromise();
  }

  /** KPI bundle for the dashboard. Empty bundle when feature off. */
  getMetrics(topN = 5): Observable<UpsellMetrics | null> {
    if (!this.isEnabled()) return of(null);
    const orgUuid = this.getOrgUuid();
    if (!orgUuid) return of(null);
    const params = new HttpParams().set('topN', String(topN));
    return this.http
      .get<UpsellMetrics>(`${environment.apiUrl}/upsell/${orgUuid}/metrics`, { params })
      .pipe(catchError(() => of(null)));
  }
}
